/**
 * Parsing, matching and display of key combinations.
 *
 * Combination syntax: modifiers joined by "+", ending in one non-modifier key.
 * `Mod` resolves to ⌘ on Apple platforms and Ctrl everywhere else.
 *
 *   "Mod+K"  "Mod+Shift+H"  "Mod+Alt+1"  "Escape"  "Mod+\\"
 */

export interface ParsedKeys {
  mod: boolean;
  alt: boolean;
  shift: boolean;
  /** The non-modifier key, lower-cased. */
  key: string;
}

/**
 * Deliberately not memoised. A regex over a short string costs nothing, and a
 * cached first answer would be the wrong one for the whole session if the first
 * call happened to land before `navigator` existed.
 *
 * `navigator.platform` is deprecated but remains the only signal available in
 * every browser we support — `userAgentData` is Chromium-only.
 */
export function isApplePlatform(): boolean {
  if (typeof navigator === "undefined") return false;
  return /Mac|iPhone|iPad|iPod/.test(navigator.platform);
}

/** Aliases so bindings can be written readably. */
const KEY_ALIASES: Record<string, string> = {
  esc: "escape",
  space: " ",
  spacebar: " ",
  up: "arrowup",
  down: "arrowdown",
  left: "arrowleft",
  right: "arrowright",
  del: "delete",
  return: "enter",
  backslash: "\\",
};

export function parseKeys(keys: string): ParsedKeys {
  const parts = keys.split("+").map((p) => p.trim()).filter(Boolean);
  const parsed: ParsedKeys = { mod: false, alt: false, shift: false, key: "" };

  for (const part of parts) {
    const lower = part.toLowerCase();
    if (lower === "mod" || lower === "cmd" || lower === "ctrl" || lower === "meta") {
      parsed.mod = true;
    } else if (lower === "alt" || lower === "option" || lower === "opt") {
      parsed.alt = true;
    } else if (lower === "shift") {
      parsed.shift = true;
    } else {
      parsed.key = KEY_ALIASES[lower] ?? lower;
    }
  }

  if (!parsed.key) {
    throw new Error(`Shortcut "${keys}" has modifiers but no key`);
  }
  return parsed;
}

/** Canonical form used as the registry's collision key. */
export function normalizeKeys(keys: string): string {
  const p = parseKeys(keys);
  return [p.mod && "mod", p.alt && "alt", p.shift && "shift", p.key]
    .filter(Boolean)
    .join("+");
}

/**
 * Whether the pressed key is the one the binding asked for.
 *
 * Letters and digits are checked against `event.code` as well as `event.key`,
 * because a held Alt rewrites `event.key` into the composed character —
 * ⌘⌥1 arrives as key "¡" on an Apple layout, which no naive comparison catches.
 * `event.code` is physical-layout dependent, so it is an additional way to
 * match rather than a replacement.
 */
function keyMatches(event: KeyboardEvent, expected: string): boolean {
  const pressed = event.key?.toLowerCase() ?? "";
  if (pressed === expected) return true;

  if (/^[a-z]$/.test(expected)) {
    return event.code === `Key${expected.toUpperCase()}`;
  }
  if (/^[0-9]$/.test(expected)) {
    return event.code === `Digit${expected}` || event.code === `Numpad${expected}`;
  }
  return false;
}

/**
 * Modifier comparison is exact: a binding without Shift must not fire on a
 * Shift-ed press, otherwise ⌘K would swallow ⌘⇧K.
 */
export function matchesEvent(event: KeyboardEvent, parsed: ParsedKeys): boolean {
  const modPressed = isApplePlatform() ? event.metaKey : event.ctrlKey;
  const otherModPressed = isApplePlatform() ? event.ctrlKey : event.metaKey;

  if (parsed.mod !== modPressed) return false;
  if (otherModPressed) return false;
  if (parsed.alt !== event.altKey) return false;
  if (parsed.shift !== event.shiftKey) return false;

  return keyMatches(event, parsed.key);
}

const DISPLAY_NAMES: Record<string, string> = {
  " ": "Space",
  escape: "Esc",
  enter: "Enter",
  backspace: "Backspace",
  delete: "Del",
  arrowup: "↑",
  arrowdown: "↓",
  arrowleft: "←",
  arrowright: "→",
  "\\": "\\",
};

/** Human-readable form for menus and help UI: "⌘⇧K" on Apple, "Ctrl+Shift+K" elsewhere. */
export function formatKeys(keys: string): string {
  const p = parseKeys(keys);
  const keyName = DISPLAY_NAMES[p.key] ?? p.key.toUpperCase();

  if (isApplePlatform()) {
    return [p.mod && "⌘", p.alt && "⌥", p.shift && "⇧", keyName].filter(Boolean).join("");
  }
  return [p.mod && "Ctrl", p.alt && "Alt", p.shift && "Shift", keyName]
    .filter(Boolean)
    .join("+");
}

/**
 * TipTap keymap form, for bindings delivered through ProseMirror.
 * TipTap expects "Mod-Shift-h" style with single-character keys lower-cased.
 */
export function toProseMirrorKeys(keys: string): string {
  const p = parseKeys(keys);
  const keyName =
    p.key === " "
      ? "Space"
      : p.key.length === 1
        ? p.key
        : p.key.charAt(0).toUpperCase() + p.key.slice(1);

  return [p.mod && "Mod", p.alt && "Alt", p.shift && "Shift", keyName]
    .filter(Boolean)
    .join("-");
}
